import { createClient } from '@supabase/supabase-js';
import { validateEnvironment, logEnvironmentInfo } from '../utils/envValidation';
import { logger } from '../utils/logger';

// Validate environment configuration before creating client
const envConfig = validateEnvironment();

// Log environment info in development
if (envConfig.environment === 'development') {
  logEnvironmentInfo();
}

export const supabase = createClient(envConfig.supabaseUrl, envConfig.supabaseAnonKey, {
  auth: {
    // Enhanced auth configuration for security
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Set secure storage options
    storage: typeof window !== 'undefined' ? window.localStorage : undefined
  },
  global: {
    // Add headers for better security
    headers: {
      'X-Client-Info': 'hg-training-platform'
    }
  }
});

// Types for our Supabase tables
export interface DbUser {
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: 'user' | 'admin';
  department?: string | null;
  job_title?: string | null;
  line_manager?: string | null;
  created_at?: string;
}

export interface DbQuiz {
  id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  title: string;
  description: string;
  passing_score: number;
  media_url?: string;
  is_enabled: boolean;
  is_hidden: boolean;
  order: number;
  has_introduction?: boolean;
  introduction_title?: string;
  introduction_content?: string;
  introduction_media_url?: string;
  /** Seconds before Start Module appears. 0 = no minimum. */
  introduction_min_read_time?: number;
  /** Minutes allowed from Start Module to finish. null = no limit. */
  time_limit_minutes?: number | null;
  assigned_users?: string[];
  introduction_audio_url?: string;
  introduction_audio_duration?: number;
  audio_generated_at?: string;
  // Months a pass stays valid; drives both the certificate's expiry and the
  // automatic refresher re-opening. NULL = once-and-done.
  renewal_months?: number | null;
  // New starters get this module automatically, following its rollout by
  // department (queue_new_starter_assignments). false = never.
  auto_assign_new_starters?: boolean;
  auto_assign_due_days?: number;
  questions: DbQuestion[];
}

export type QuestionType = 'multiple-choice' | 'two_truths_and_a_lie' | 'truefalse' | 'video' | 'information' | 'policy' | 'click-to-reveal' | 'click-to-reveal-images' | 'drag-and-drop' | 'hover-reveal' | 'click-for-answer';

export interface HotspotData {
  x: number;
  y: number;
  w: number;
  h: number;
  explanation?: string;
}

export type DifferenceHotspotData = HotspotData;

export interface DbQuestion {
  id: string;
  quiz_id: string;
  type: QuestionType;
  text: string;
  thumbnail_url?: string;
  options?: string[];
  correct_options?: string[];
  /** Only on rows from learner_questions(), which leaves correct_options out
   *  of marked questions but keeps how many there are. */
  correct_count?: number;
  explanation?: string;
  number_of_guesses?: number;
  hotspots?: HotspotData[];
  difference_url?: string;
  difference_hotspots?: DifferenceHotspotData[];
  video_url?: string;
  video_type?: string;
  video_start_time?: number;
  video_end_time?: number;
  created_at?: string;
  order?: number;
  minimum_read_time?: number;
  weight?: number;
  image_orientation?: 'landscape' | 'portrait';
  policy_url?: string;
  // Audio support fields
  question_audio_url?: string;
  explanation_audio_url?: string;
  question_audio_duration?: number;
  explanation_audio_duration?: number;
  use_audio?: boolean;
  // Video question specific audio fields
  video_intro_audio_url?: string;
  video_intro_audio_duration?: number;
  video_question_audio_url?: string;
  video_question_audio_duration?: number;
  key_takeaways_heading?: string;
  // Click for Answer fields
  answer_question?: string;
  answer_text?: string;
  // Stale audio detection
  audio_text_hash?: string | null;
}

export interface DbQuizDraft {
  id: string;
  quiz_id: string | null;
  created_by: string;
  draft_data: Record<string, unknown>;
  title: string;
  question_count: number;
  current_step: number;
  created_at: string;
  updated_at: string;
}

export interface DbAudioGenerationJob {
  id: string;
  quiz_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_items: number;
  completed_items: number;
  failed_items: number;
  error_message?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface DbQuizAttempt {
  id: string;
  user_id: string;
  quiz_id: string;
  score: number;
  completed: boolean;
  answers: Record<string, any>;
  started_at: string;
  completed_at?: string;
  status?: 'in_progress' | 'completed' | 'cancelled' | 'timed_out';
  current_question_index?: number;
  /** Times the page lost focus while a question was showing. */
  focus_losses?: number;
  /** Device types worked on, in order; null = before 13 Oct 2026 (computer). */
  devices?: ('phone' | 'tablet' | 'computer')[] | null;
}

export interface DbCertificate {
  id: string;
  user_id: string;
  quiz_id: string;
  name: string;
  email: string;
  quiz_title: string;
  score: number;
  passing_score: number;
  passed: boolean;
  date_completed: string;
  certificate_id: string;
  attempt_id: string | null;
  // Server-side generation lifecycle. storage_path is null until the PDF has
  // been rendered into the private certificates bucket; downloads go through
  // the get-certificate-url edge function, never a direct storage URL.
  storage_path: string | null;
  issued_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  revoked_by: string | null;
  generation_status: 'pending' | 'generating' | 'issued' | 'failed';
  generation_error: string | null;
  generation_attempts: number;
  created_at: string;
  updated_at: string;
}

// Initialize storage bucket for quiz media
export const initializeStorage = async () => {
  try {
    // For quiz audio playback, we don't need full storage API access
    // The bucket exists and is publicly accessible, so we'll just verify basic connectivity
    const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();

    if (bucketsError) {
      logger.warn('Storage API access limited, but quiz media should still work:', bucketsError.message);
      // Don't return false - audio can still work with direct URLs
      return true;
    }

    const quizMediaBucket = buckets?.find(b => b.name === 'quiz-media');

    if (!quizMediaBucket) {
      logger.warn('Quiz media bucket not found via API, but public URLs should still work.');
      // Don't return false - the bucket exists, we might just not have list permissions
      return true;
    }

    logger.log('Storage bucket initialized successfully');
    return true;
  } catch (error) {
    logger.warn('Storage initialization had issues, but quiz media should still work:', error);
    // Don't fail completely - audio URLs should still work even if we can't list buckets
    return true;
  }
}; 